import { supabase } from './supabase';
import type {
  CreateRenderRequest,
  CreateRenderResponse,
  ErrorBody,
} from '@archi/types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

class ApiError extends Error {
  constructor(readonly body: ErrorBody, readonly status: number) {
    super(body.message);
    this.name = 'ApiError';
  }
}

// Bypass supabase-js functions.invoke (silent fail in production).
// Direct fetch to edge function with current session token.
const invokeFn = async <T>(name: string, body: unknown): Promise<T> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    throw new ApiError({ code: 'UNAUTHORIZED', message: 'Sem sessão ativa' }, 401);
  }
  let res: Response;
  try {
    res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Falha de rede';
    throw new ApiError({ code: 'NETWORK', message: msg }, 0);
  }
  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { /* ignore */ }
  if (!res.ok) {
    const errBody: ErrorBody = (parsed && typeof parsed === 'object'
      ? parsed as ErrorBody
      : { code: 'INTERNAL', message: text || `HTTP ${res.status}` });
    throw new ApiError(errBody, res.status);
  }
  return parsed as T;
};

export const createRender = (req: CreateRenderRequest): Promise<CreateRenderResponse> =>
  invokeFn('create-render', req);

export const bootstrapUser = (): Promise<{ ok: true; credits: number }> =>
  invokeFn('bootstrap-user', {});

export const signedRenderUrl = async (output_key: string): Promise<string> => {
  const { data, error } = await supabase.storage
    .from('renders')
    .createSignedUrl(output_key, 60 * 60);
  if (error || !data) throw new Error(error?.message ?? 'sign failed');
  return data.signedUrl;
};

export const uploadInput = async (
  userId: string,
  file: File,
): Promise<string> => {
  // 8-char random + original name is searchable, not predictable.
  const rand = crypto.randomUUID().slice(0, 8);
  const cleanName = file.name.replace(/[^A-Za-z0-9.\-_]/g, '_').slice(0, 64);
  const key = `${userId}/${rand}_${cleanName}`;
  const { error } = await supabase.storage.from('inputs').upload(key, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type,
  });
  if (error) throw error;
  return key;
};

export { ApiError };
